import { Check } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

interface SelectableCardBaseProps {
  title: ReactNode;
  description?: ReactNode;
  /** Duza ikona nad tytulem (uklad `stacked`). */
  icon?: ReactNode;
  disabled?: boolean;
  /**
   * `stacked` — ikona i tekst wysrodkowane, jak karty wyboru sposobu losowania;
   * `inline` — znacznik po lewej, tekst po prawej, jak „Walkower / Krecz”.
   */
  layout?: 'stacked' | 'inline';
  className?: string;
}

export interface RadioCardProps extends SelectableCardBaseProps {
  name: string;
  value: string;
  checked: boolean;
  onSelect: (value: string) => void;
}

/**
 * Duza karta wyboru jednokrotnego — „Losowanie całkowite / Rozstawienie wg rankingu /
 * Rozstawienie ręczne / Import rozstawienia”.
 *
 * Pod spodem jest natywny `input[type=radio]`, wiec strzalki, `Tab` i czytniki
 * ekranu dzialaja bez dodatkowego kodu. Wizualnie input jest przezroczysty
 * i rozciagniety na cala karte.
 */
export function RadioCard({
  name,
  value,
  checked,
  onSelect,
  title,
  description,
  icon,
  disabled = false,
  layout = 'stacked',
  className,
}: RadioCardProps) {
  const id = useId();

  return (
    <label
      htmlFor={id}
      className={cn(
        'group relative flex rounded-xl border p-4 transition-colors duration-150',
        layout === 'stacked' ? 'flex-col items-center gap-2 text-center' : 'items-start gap-3',
        checked
          ? 'border-brand bg-brand-subtle'
          : 'border-border bg-surface hover:border-border-strong hover:bg-surface-hover',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        'has-[:focus-visible]:ring-brand/35 has-[:focus-visible]:ring-2',
        className,
      )}
    >
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onSelect(value)}
        className="sr-only"
      />

      <span
        aria-hidden="true"
        className={cn(
          'grid size-[1.125rem] shrink-0 place-items-center rounded-full border transition-colors',
          layout === 'stacked' ? 'absolute top-4 left-4' : '',
          checked ? 'border-brand-700' : 'border-border-strong bg-surface-raised',
        )}
      >
        {checked && <span className="bg-brand-700 size-2.5 rounded-full" />}
      </span>

      {icon && (
        <span
          aria-hidden="true"
          className={cn('mt-2 [&_svg]:size-8', checked ? 'text-brand-700' : 'text-fg-muted')}
        >
          {icon}
        </span>
      )}

      <span className="flex min-w-0 flex-col gap-1">
        <span className="text-fg-strong text-sm font-bold">{title}</span>
        {description && <span className="text-fg-muted text-xs leading-relaxed">{description}</span>}
      </span>
    </label>
  );
}

export interface CheckboxCardProps extends SelectableCardBaseProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
}

/**
 * Karta wyboru wielokrotnego — „Walkower / Krecz / Przerwa medyczna /
 * Mecz zakończony” z ekranu wprowadzania wyniku.
 */
export function CheckboxCard({
  checked,
  onCheckedChange,
  title,
  description,
  disabled = false,
  className,
}: CheckboxCardProps) {
  const id = useId();

  return (
    <label
      htmlFor={id}
      className={cn(
        'flex items-start gap-3 rounded-xl border p-3.5 transition-colors duration-150',
        checked
          ? 'border-brand bg-brand-subtle'
          : 'border-border bg-surface hover:border-border-strong hover:bg-surface-hover',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        'has-[:focus-visible]:ring-brand/35 has-[:focus-visible]:ring-2',
        className,
      )}
    >
      <input
        id={id}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onCheckedChange(event.target.checked)}
        className="sr-only"
      />

      <span
        aria-hidden="true"
        className={cn(
          'mt-0.5 grid size-[1.125rem] shrink-0 place-items-center rounded-[0.3125rem] border transition-colors',
          checked ? 'border-brand bg-brand' : 'border-border-strong bg-surface-raised',
        )}
      >
        {checked && <Check className="text-fg-on-brand size-3" strokeWidth={3.5} />}
      </span>

      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-fg-strong text-sm font-semibold">{title}</span>
        {description && <span className="text-fg-muted text-xs leading-relaxed">{description}</span>}
      </span>
    </label>
  );
}
